import React, { createContext, useEffect, useReducer } from "react"

/**
 * Cart state for the shop flow (product pages -> cart -> checkout).
 *
 * The cart lives in a single React Context so any page can read `cartData`
 * and mutate it through the exposed helpers. State is mirrored into
 * localStorage so a cart survives a reload or a client-side navigation, and
 * every storage access is guarded for SSR (no `window` at build time) and for
 * private-browsing modes that throw on access.
 */

const STORAGE_KEY = "pss_cart"

export const Context = createContext()

// A cart item is keyed by `id`. Shape expected by the cart/checkout pages:
//   { id, name, title, price, count, image: { gatsbyImageData }, skuNumber, url }
const readStoredCart = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    // Corrupt JSON or a storage access that threw; start from an empty cart
    // rather than crashing the whole app.
    return []
  }
}

const writeStoredCart = cart => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cart))
  } catch (e) {
    // Persistence is best-effort; the in-memory cart still works without it.
  }
}

const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD": {
      const { item } = action
      const existing = state.find(entry => entry.id === item.id)
      if (existing) {
        return state.map(entry =>
          entry.id === item.id
            ? { ...entry, count: entry.count + (item.count || 1) }
            : entry
        )
      }
      return [...state, { ...item, count: item.count || 1 }]
    }
    case "INCREASE":
      return state.map(entry =>
        entry.id === action.id ? { ...entry, count: entry.count + 1 } : entry
      )
    case "DECREASE":
      // Removes the line when it would drop to zero.
      return state
        .map(entry =>
          entry.id === action.id
            ? { ...entry, count: entry.count - 1 }
            : entry
        )
        .filter(entry => entry.count > 0)
    case "REMOVE":
      return state.filter(entry => entry.id !== action.id)
    case "CLEAR":
      return []
    default:
      return state
  }
}

const CartProvider = ({ children }) => {
  const [cartData, dispatch] = useReducer(cartReducer, [], readStoredCart)

  // Keep localStorage in sync with every cart change.
  useEffect(() => {
    writeStoredCart(cartData)
  }, [cartData])

  const addCartItem = item => dispatch({ type: "ADD", item })
  const increaseCartItem = id => dispatch({ type: "INCREASE", id })
  const decreaseCartItem = id => dispatch({ type: "DECREASE", id })
  const removeCartItem = id => dispatch({ type: "REMOVE", id })
  const clearCart = () => dispatch({ type: "CLEAR" })

  return (
    <Context.Provider
      value={{
        cartData,
        addCartItem,
        increaseCartItem,
        decreaseCartItem,
        removeCartItem,
        clearCart,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export default CartProvider
